import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { Text, Icon, Modal } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { DevProject } from '../../types/chat';
import projectEnvironmentApi, {
  EnvironmentDeployCheckResult,
  EnvironmentRuntime,
  ProjectEnvironment,
} from '../../services/projectEnvironment';
import { usePolling } from '../../hooks/usePolling';
import { View, displayEnvironmentName, extractErrorDetail, isBusyStatus } from './types';
import { EnvironmentListView } from './EnvironmentListView';
import { EnvironmentDetailsView } from './EnvironmentDetailsView';
import { EnvironmentFormView } from './EnvironmentFormView';

// A create finishes in about a second and a deploy in twenty, so this one
// earns its speed — and it only runs while something is actually in flight.
const RECORD_POLL_INTERVAL_MS = 7_000;

// Runtime status is one `docker ps` for every environment, cached 15s
// server-side (PS_CACHE_TTL_SECONDS). Idling faster than that would just
// re-read the same cached answer, so 20s idle; 5s only while a start or stop
// is actually running, where the user is waiting on the result.
const RUNTIME_POLL_IDLE_MS = 20_000;
const RUNTIME_POLL_TRANSITIONING_MS = 5_000;

interface EnvironmentsPanelProps {
  project: DevProject | null;
  onClose: () => void;
  /** True while an agent run is active — deploys are rejected server-side then. */
  isBuilding?: boolean;
}

/** Slide-over panel to manage a project's environments (list / details / create-edit). */
export const EnvironmentsPanel: React.FC<EnvironmentsPanelProps> = ({
  project,
  onClose,
  isBuilding = false,
}) => {
  const { projectId } = useParams();
  const [environments, setEnvironments] = useState<ProjectEnvironment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [view, setView] = useState<View>({ kind: 'list' });
  const [listActionError, setListActionError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [checkingDeployId, setCheckingDeployId] = useState<string | null>(null);
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [deployConfirm, setDeployConfirm] = useState<{
    environment: ProjectEnvironment;
    check: EnvironmentDeployCheckResult;
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<ProjectEnvironment | null>(null);
  const [runtime, setRuntime] = useState<Record<string, EnvironmentRuntime>>({});
  const [runtimeActionId, setRuntimeActionId] = useState<string | null>(null);

  useEffect(() => {
    fetchEnvironments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const applyEnvironments = (next: ProjectEnvironment[]) => {
    setEnvironments(next);
    // Keep an open details view in sync; drop it if the environment is gone.
    setView((v) => {
      if (v.kind !== 'details') return v;
      const refreshed = next.find((e) => e.id === v.environment.id);
      return refreshed ? { kind: 'details', environment: refreshed } : { kind: 'list' };
    });
  };

  const fetchEnvironments = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await projectEnvironmentApi.listEnvironments(projectId);
      applyEnvironments(data.environments || []);
    } catch (e) {
      console.error('Failed to fetch environments:', e);
      setError('Failed to load environments');
    } finally {
      setLoading(false);
    }
  };

  // Deploy answers 202 and finishes in the background. A switch rather than a
  // cadence: it runs while a deploy is in flight and stops dead when none is.
  // Create needs none of this — it completes inside its own request.
  const anyBusy = environments.some((e) => isBusyStatus(e.status));
  usePolling<null>({
    enabled: !!projectId && anyBusy,
    interval: () => RECORD_POLL_INTERVAL_MS,
    fetch: async () => {
      if (!projectId) return null;
      try {
        const data = await projectEnvironmentApi.listEnvironments(projectId);
        applyEnvironments(data.environments || []);
      } catch (e) {
        console.error('Failed to poll environments:', e);
      }
      return null;
    },
  });

  // Live container state, polled while the panel is open. Deliberately kept
  // separate from the record's own status — an environment can be 'deployed'
  // and not running (e.g. after a host reboot).
  const hasEnvironments = environments.length > 0;
  const { refreshNow: refreshRuntime } = usePolling<Record<string, EnvironmentRuntime>>({
    enabled: !!projectId && hasEnvironments,
    fetch: async () => {
      if (!projectId) return null;
      try {
        const data = await projectEnvironmentApi.getRuntimeStatus(projectId);
        const next = data.environments || {};
        setRuntime(next);
        return next;
      } catch (e) {
        console.error('Failed to fetch environment runtime status:', e);
        // Pace from the last good reading rather than dropping to idle — a
        // failed poll during a start is exactly when speed still matters.
        return runtime;
      }
    },
    interval: (rt) =>
      rt &&
      Object.values(rt).some(
        (r) => r.in_progress || r.status === 'starting' || r.status === 'stopping'
      )
        ? RUNTIME_POLL_TRANSITIONING_MS
        : RUNTIME_POLL_IDLE_MS,
  });

  const handleStartStop = async (environment: ProjectEnvironment, action: 'start' | 'stop') => {
    if (!projectId) return;
    try {
      setListActionError(null);
      setRuntimeActionId(environment.id);
      const call =
        action === 'start'
          ? projectEnvironmentApi.startEnvironment
          : projectEnvironmentApi.stopEnvironment;
      const result = await call(projectId, environment.id);
      // Flip the badge immediately; the poll takes over from here.
      setRuntime((prev) => ({
        ...prev,
        [environment.id]: {
          status: result.status,
          in_progress: true,
          services: prev[environment.id]?.services || [],
        },
      }));
      // Re-read and re-pace now. The timer in flight was scheduled at the idle
      // cadence, so without this the first confirmation could be 20s away.
      refreshRuntime();
    } catch (e: any) {
      setListActionError(
        extractErrorDetail(e, `Failed to ${action} environment`)
      );
    } finally {
      setRuntimeActionId(null);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  // A create chains straight into the deploy flow — a created environment is
  // only a reservation until deployed. A rename/domain change does not: nothing
  // about its code changed, and redeploying is the Deploy button's job.
  const handleSaved = async (saved: ProjectEnvironment, created: boolean) => {
    setView({ kind: 'list' });
    if (!projectId) return;
    try {
      const data = await projectEnvironmentApi.listEnvironments(projectId);
      applyEnvironments(data.environments || []);
    } catch (e) {
      console.error('Failed to fetch environments:', e);
    }
    if (created) await handleDeployClick(saved);
  };

  // Deploy is a two-call flow: deploy-check first, then the confirmation
  // dialog, then POST /deploy (with confirm_data_loss on a backward check).
  //
  // The dialog opens only when the check reports something migration-related —
  // data at risk or migrations to list. A purely informational message (e.g.
  // "nothing has been built here yet") is not a question, so nothing is asked.
  const handleDeployClick = async (environment: ProjectEnvironment) => {
    if (!projectId) return;
    try {
      setListActionError(null);
      setCheckingDeployId(environment.id);
      const check = await projectEnvironmentApi.deployCheck(projectId, environment.id);
      setDeployError(null);

      const needsConfirmation =
        check.data_loss ||
        check.pending_migrations.length > 0 ||
        check.removed_migrations.length > 0;

      if (!needsConfirmation) {
        await startDeploy(environment, check);
        return;
      }
      setDeployConfirm({ environment, check });
    } catch (e: any) {
      setListActionError(extractErrorDetail(e, 'Failed to check deploy'));
    } finally {
      setCheckingDeployId(null);
    }
  };

  /** The POST itself, shared by the confirmed and the skipped-dialog paths.
   *  Throws so each caller reports the failure where the user is looking —
   *  inside the dialog, or on the list. */
  const startDeploy = async (
    environment: ProjectEnvironment,
    check: EnvironmentDeployCheckResult
  ) => {
    await projectEnvironmentApi.deploy(projectId!, environment.id, check.data_loss);
    // Functional updates — this also runs right after a save refreshed the
    // list, where a mapped copy of the closure's list would be stale.
    setEnvironments((prev) =>
      prev.map((e) => (e.id === environment.id ? { ...e, status: 'deploying' } : e))
    );
    setView((v) =>
      v.kind === 'details' && v.environment.id === environment.id
        ? { kind: 'details', environment: { ...v.environment, status: 'deploying' } }
        : v
    );
  };

  const confirmDeploy = async () => {
    if (!projectId || !deployConfirm) return;
    const { environment, check } = deployConfirm;
    try {
      setDeploying(true);
      setListActionError(null);
      setDeployError(null);
      await startDeploy(environment, check);
      setDeployConfirm(null);
    } catch (e: any) {
      // Kept open deliberately. Closing on failure hides the reason behind a
      // dialog that has already gone, and on a destructive deploy the user has
      // just read a warning they now cannot act on. They dismiss it themselves.
      setDeployError(extractErrorDetail(e, 'Failed to start deploy'));
    } finally {
      setDeploying(false);
    }
  };

  const confirmDelete = async () => {
    if (!projectId || !deleteConfirm) return;
    const environment = deleteConfirm;
    setDeleteConfirm(null);
    try {
      setListActionError(null);
      setDeletingId(environment.id);
      await projectEnvironmentApi.deleteEnvironment(projectId, environment.id);
      setEnvironments((prev) => prev.filter((e) => e.id !== environment.id));
      setView({ kind: 'list' });
    } catch (e: any) {
      setListActionError(extractErrorDetail(e, 'Failed to delete environment'));
    } finally {
      setDeletingId(null);
    }
  };

  const headerTitle =
    view.kind === 'form'
      ? view.environment
        ? 'Edit Environment'
        : 'Create Environment'
      : view.kind === 'details'
      ? displayEnvironmentName(view.environment.environment_name)
      : 'Environments';

  const headerSubtitle =
    view.kind === 'form'
      ? view.environment
        ? 'Rename this environment'
        : 'Run a copy of this project on its own URL'
      : view.kind === 'details'
      ? view.environment.url
      : 'Test copies of this project, each on its own URL';

  return (
    <>
      <div
        className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
        onClick={handleClose}
      >
        <div
          className={`absolute right-0 top-0 h-full bg-bg-primary w-[480px] flex flex-col border-l border-border-secondary-alt ${
            isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'
          }`}
          onClick={(e) => e.stopPropagation()}
          style={{
            boxShadow:
              '0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)',
          }}
        >
          {/* Header */}
          <div className="bg-bg-primary box-border flex gap-bg-v2-md items-start p-bg-v2-3xl w-full">
            <div className="flex flex-[1_0_0] gap-bg-v2-md items-start min-h-px min-w-px">
              {view.kind !== 'list' && (
                <button
                  onClick={() => setView({ kind: 'list' })}
                  className="flex items-center justify-center rounded-full w-8 h-8 text-fg-secondary hover:bg-bg-primary-hover transition-all duration-200 shrink-0"
                  title="Back"
                >
                  <Icon name={IconName.ArrowLeft} size="sm" inheritColor />
                </button>
              )}
              <div className="flex flex-[1_0_0] flex-col gap-bg-v2-xxs items-start min-h-px min-w-px whitespace-pre-wrap">
                <Text variant="text-lg" weight="semibold" color="primary" className="w-full">
                  {headerTitle}
                </Text>
                <Text variant="text-sm" weight="regular" color="secondary" className="w-full break-words">
                  {headerSubtitle}
                </Text>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="flex items-center justify-center rounded-full w-8 h-8 text-fg-secondary hover:bg-bg-primary-hover transition-all duration-200 absolute right-bg-v2-lg top-bg-v2-lg"
            >
              <Icon name={IconName.Cross} size="md" inheritColor />
            </button>
          </div>

          {/* Divider */}
          <div className="box-border flex flex-col gap-bg-v2-xl items-center px-bg-v2-3xl py-0 w-full">
            <div className="bg-border-secondary h-px w-full" />
          </div>

          {/* Content */}
          <div className="box-border flex flex-col gap-bg-v2-xl px-bg-v2-3xl py-bg-v2-xl w-full flex-1 overflow-y-auto">
            {view.kind === 'list' && (
              <EnvironmentListView
                loading={loading}
                error={error}
                environments={environments}
                runtime={runtime}
                runtimeActionId={runtimeActionId}
                deletingId={deletingId}
                actionError={listActionError}
                isBuilding={isBuilding}
                onStartStop={handleStartStop}
                onRetry={fetchEnvironments}
                onEdit={(environment) => setView({ kind: 'form', environment })}
                onDelete={(environment) => setDeleteConfirm(environment)}
                onOpenDetails={(environment) => setView({ kind: 'details', environment })}
                onCreate={() => setView({ kind: 'form' })}
                project={project}
              />
            )}

            {view.kind === 'details' && (
              <EnvironmentDetailsView
                environment={view.environment}
                runtime={runtime[view.environment.id]}
                runtimeActionPending={runtimeActionId === view.environment.id}
                checkingDeploy={checkingDeployId === view.environment.id}
                deleting={deletingId === view.environment.id}
                isBuilding={isBuilding}
                deployLocked={environments.some(
                  (e) => e.id !== view.environment.id && isBusyStatus(e.status)
                )}
                onStartStop={(action) => handleStartStop(view.environment, action)}
                onDeploy={() => handleDeployClick(view.environment)}
                onEdit={() => setView({ kind: 'form', environment: view.environment })}
                onDelete={() => setDeleteConfirm(view.environment)}
                project={project}
              />
            )}

            {view.kind === 'form' && projectId && (
              <EnvironmentFormView
                projectId={projectId}
                domainPrefix={project?.custom_domain || projectId}
                environment={view.environment}
                onSaved={handleSaved}
                onCancel={() =>
                  setView(
                    view.environment
                      ? { kind: 'details', environment: view.environment }
                      : { kind: 'list' }
                  )
                }
              />
            )}
            </div>
          </div>
        </div>

      {/* Portaled to <body> for two reasons. The slide-over animates with a CSS
          transform, which would otherwise trap a fixed overlay. And they are
          rendered as SIBLINGS of the backdrop, not children: a React portal
          still bubbles events through the React tree, so a dialog nested inside
          the backdrop would send every click — Cancel, Deploy, even its own
          close button — straight into the panel's onClick and dismiss the whole
          panel underneath it. */}
      {deployConfirm &&
        createPortal(
          <Modal
            isOpen={!!deployConfirm}
            onClose={() => {
              if (deploying) return;
              setDeployError(null);
              setDeployConfirm(null);
            }}
            title={
              deployConfirm.check.data_loss
                ? 'Deploy and reset environment data?'
                : `Deploy to '${displayEnvironmentName(deployConfirm.environment.environment_name)}'?`
            }
            size="sm"
            onCancel={() => {
              setDeployError(null);
              setDeployConfirm(null);
            }}
            onSubmit={confirmDeploy}
            submitLabel={
              deploying ? 'Deploying…' : deployConfirm.check.data_loss ? 'Deploy & reset data' : 'Deploy'
            }
            submitDisabled={deploying}
            submitLoading={deploying}
            submitVariant={deployConfirm.check.data_loss ? 'error' : 'primary'}
          >
            <div className="flex flex-col gap-bg-v2-md">
              <Text variant="text-xs" color={deployConfirm.check.data_loss ? 'error' : 'secondary'}>
                {deployConfirm.check.message}
              </Text>
              {deployConfirm.check.removed_migrations.length > 0 && (
                <div className="flex flex-col gap-bg-v2-xxs">
                  <Text variant="text-xs" weight="medium" color="tertiary">
                    Migrations this environment has that the incoming code does not
                  </Text>
                  {deployConfirm.check.removed_migrations.map((migration) => (
                    <Text key={migration} variant="text-xs" color="error" className="font-mono">
                      {migration}
                    </Text>
                  ))}
                </div>
              )}
              {deployConfirm.check.pending_migrations.length > 0 && (
                <div className="flex flex-col gap-bg-v2-xxs">
                  <Text variant="text-xs" weight="medium" color="tertiary">
                    Pending migrations
                  </Text>
                  {deployConfirm.check.pending_migrations.map((migration) => (
                    <Text key={migration} variant="text-xs" color="secondary" className="font-mono">
                      {migration}
                    </Text>
                  ))}
                </div>
              )}
              {deployError && (
                <div className="flex items-start gap-bg-v2-xs p-bg-v2-md bg-bg-error-primary border border-border-error-subtle rounded-bg-v2-sm">
                  <Icon
                    name={IconName.Alert}
                    size="sm"
                    className="text-text-error-primary shrink-0 mt-bg-v2-xxs"
                    inheritColor
                  />
                  <Text variant="text-xs" color="error" className="break-words">
                    {deployError}
                  </Text>
                </div>
              )}
            </div>
          </Modal>,
          document.body
        )}

      {deleteConfirm &&
        createPortal(
          <Modal
            isOpen={!!deleteConfirm}
            onClose={() => setDeleteConfirm(null)}
            title={`Delete environment '${displayEnvironmentName(deleteConfirm.environment_name)}'?`}
            size="sm"
            onCancel={() => setDeleteConfirm(null)}
            onSubmit={confirmDelete}
            submitLabel="Delete"
            submitVariant="error"
          >
            <Text variant="text-xs" color="secondary">
              Its containers will be stopped, its URL will stop working and all of its data will be
              permanently removed. This cannot be undone.
            </Text>
          </Modal>,
          document.body
        )}
    </>
  );
};

export default EnvironmentsPanel;
